import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { SkeletonTheme } from 'react-loading-skeleton'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { FullLoader } from 'ui'
import { AppRoutes } from 'pages/routes'
import { Modals } from 'features/modal/modals'
import { SplashScreen } from './splash-screen'
import { useAuthContext } from '../../core/providers/AuthProvider'
import { useDispatch } from 'react-redux'
import { socketClient, authService } from '../../core/services'
import { openWindow } from 'models/window/actions'
import { modalNames } from 'features/modal/constants/names'
import {
    shouldShowDesktopStub,
    getPlatformInfo,
} from '../../core/helpers/platform-detect'
import { routes } from '../../core/routes/paths'
import Toasts from '../common/toasts/toasts'
import 'react-loading-skeleton/dist/skeleton.css'

const App = () => {
    const { t, i18n } = useTranslation()
    const { isAuthenticated, isLoading, refreshUser } = useAuthContext()
    const [showSplash, setShowSplash] = useState(true)
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()

    const handleSplashComplete = () => {
        setShowSplash(false)
    }

    // Автоопределение языка из Telegram WebApp или браузера
    // ПРИОРИТЕТ: 1. manual (юзер выбрал вручную) 2. Telegram WebApp 3. Browser navigator 4. fallback en
    useEffect(() => {
        let retryCount = 0
        const MAX_RETRIES = 10 // Максимум 1 секунда ожидания (10 * 100ms)
        
        const detectAndSetLanguage = async () => {
            const savedLang = localStorage.getItem('i18nextLng')
            const langSource = localStorage.getItem('languageSource')
            
            // Если юзер выбрал язык ВРУЧНУЮ в приложении - не переопределяем
            if (langSource === 'manual' && savedLang && savedLang !== 'undefined') {
                console.log('🌍 Keeping manually selected language:', savedLang)
                return
            }

            // Маппинг Telegram language codes на наши локали
            const langMap = {
                'ru': 'ru',
                'en': 'en',
                'uk': 'ua', // Украинский в Telegram = uk
                'es': 'es',
                'pt': 'pt',
                'pl': 'pl',
                'tr': 'tr',
                'ro': 'ro',
                'uz': 'uz',
            }
            const availableLangs = ['ru', 'en', 'es', 'pt', 'pl', 'tr', 'ro', 'uz', 'ua']
            
            let detectedLang = null
            let source = null

            // 1. Пробуем Telegram WebApp language
            const hasTelegramWebApp = window.Telegram?.WebApp
            const hasInitData = hasTelegramWebApp && window.Telegram.WebApp.initData
            
            if (hasTelegramWebApp) {
                const tg = window.Telegram.WebApp
                
                // Логируем что есть в Telegram WebApp (только первый раз)
                if (retryCount === 0) {
                    console.log('🔍 Telegram WebApp debug:', {
                        hasInitData: !!tg.initData,
                        hasInitDataUnsafe: !!tg.initDataUnsafe,
                        hasUser: !!tg.initDataUnsafe?.user,
                        languageCode: tg.initDataUnsafe?.user?.language_code,
                        platform: tg.platform,
                        version: tg.version
                    })
                }
                
                // Ждём ready ТОЛЬКО если есть initData (значит мы реально в Telegram)
                // Если initData пустой - это браузер, не ждём
                if (hasInitData && !tg.initDataUnsafe?.user && retryCount < MAX_RETRIES) {
                    retryCount++
                    console.log(`⏳ Waiting for Telegram WebApp to be ready... (${retryCount}/${MAX_RETRIES})`)
                    setTimeout(detectAndSetLanguage, 100)
                    return
                }

                // Если мы в Telegram и есть user - берём language_code
                if (tg.initDataUnsafe?.user) {
                    const tgLang = tg.initDataUnsafe.user.language_code
                    // Проверяем что language_code существует И не пустой
                    if (tgLang && tgLang.trim() !== '') {
                        // Нормализуем (pt-BR -> pt, en-US -> en)
                        const normalized = tgLang.toLowerCase().split('-')[0]
                        detectedLang = langMap[normalized] || normalized
                        source = 'telegram'
                        console.log('🌍 Telegram language detected:', tgLang, '→', detectedLang)
                    } else {
                        // language_code пустой - используем browser hint
                        const browserLang = navigator.language || navigator.userLanguage
                        const browserNormalized = browserLang ? browserLang.toLowerCase().split('-')[0] : null
                        
                        console.log('⚠️ Telegram user exists but no language_code, using browser hint:', browserLang)
                        
                        if (browserNormalized && (langMap[browserNormalized] || availableLangs.includes(browserNormalized))) {
                            detectedLang = langMap[browserNormalized] || browserNormalized
                            source = 'telegram-browser-hint'
                        } else {
                            detectedLang = 'en'
                            source = 'telegram-default'
                        }
                        console.log('🌍 Using fallback language:', detectedLang, '(source:', source, ')')
                    }
                }
            }
            
            // 2. Fallback на browser navigator.language (если не в Telegram или нет user data)
            if (!detectedLang) {
                const browserLang = navigator.language || navigator.userLanguage
                console.log('🌍 Browser language raw:', browserLang)
                if (browserLang) {
                    const normalized = browserLang.toLowerCase().split('-')[0]
                    detectedLang = langMap[normalized] || normalized
                    source = 'browser'
                    console.log('🌍 Browser language detected:', browserLang, '→', detectedLang)
                }
            }
            
            // 3. Проверяем что язык поддерживается
            if (detectedLang && !availableLangs.includes(detectedLang)) {
                console.log('⚠️ Language not supported, fallback to en:', detectedLang)
                detectedLang = 'en'
            }
            
            // 4. Если всё ещё нет языка - используем en
            if (!detectedLang) {
                console.log('⚠️ No language detected at all, using en')
                detectedLang = 'en'
                source = 'fallback'
            }
            
            // 5. Применяем язык
            const currentI18nLang = i18n.language
            const needsChange = detectedLang && currentI18nLang !== detectedLang
            // Синхронизируем если источник telegram, telegram-default или browser (но не fallback)
            const needsSync = isAuthenticated && source && !source.includes('fallback')
            
            console.log('🌍 Language decision:', {
                detectedLang,
                currentI18nLang,
                source,
                needsChange,
                needsSync,
                isAuthenticated
            })
            
            if (needsChange) {
                console.log('🌍 Changing language:', detectedLang, '(was:', currentI18nLang, ', source:', source, ')')
                
                i18n.changeLanguage(detectedLang)
                localStorage.setItem('i18nextLng', detectedLang)
                localStorage.setItem('languageSource', source)
            }
            
            // Синхронизируем с бэкендом (если авторизован и язык определён из Telegram/browser)
            if (needsSync) {
                authService.syncLanguage(detectedLang).then(result => {
                    if (result?.updated) {
                        console.log('✅ Language synced with backend:', result.languageCode)
                    } else {
                        console.log('ℹ️ Language already in sync:', detectedLang)
                    }
                })
            }
        }

        // Запускаем через небольшую задержку чтобы Telegram WebApp успел инициализироваться
        const timer = setTimeout(detectAndSetLanguage, 200)
        return () => clearTimeout(timer)
    }, [i18n, isAuthenticated])

    // Редирект на десктоп заглушку для ПК пользователей (кроме админов)
    const [isAdmin, setIsAdmin] = useState(null)

    useEffect(() => {
        // Проверяем является ли юзер админом (только если авторизован)
        if (isAuthenticated) {
            const checkAdmin = async () => {
                try {
                    const { adminService } = await import(
                        'core/services/adminService'
                    )
                    const response = await adminService.checkAdmin()
                    setIsAdmin(response?.isAdmin || false)
                } catch (error) {
                    setIsAdmin(false)
                }
            }
            checkAdmin()
        }
    }, [isAuthenticated])

    useEffect(() => {
        const platformInfo = getPlatformInfo()
        console.log('🖥️ Platform detected:', platformInfo)

        // Проверяем флаг обхода для разработки
        const devBypass = localStorage.getItem('devBypassDesktop') === 'true'

        // Если юзер с ПК, не админ, и не на странице /desktop - редиректим
        if (
            shouldShowDesktopStub() &&
            location.pathname !== routes.desktop &&
            isAdmin === false &&
            !devBypass
        ) {
            console.log('🔄 Redirecting desktop user to stub page')
            navigate(routes.desktop, { replace: true })
        }

        // Если админ с ПК попал на /desktop - редиректим на главную
        if (isAdmin === true && location.pathname === routes.desktop) {
            console.log('🔄 Admin on desktop page - redirecting to main')
            navigate(routes.main, { replace: true })
        }

        // Если devBypass активен и мы на /desktop - редиректим на главную
        if (devBypass && location.pathname === routes.desktop) {
            console.log('🔄 Dev bypass active - redirecting to main')
            navigate(routes.main, { replace: true })
        }
    }, [navigate, location.pathname, isAdmin])

    useEffect(() => {
        const updateAppHeight = () => {
            document.documentElement.style.setProperty(
                '--app-height',
                `${document.documentElement.clientHeight}px`
            )
        }

        updateAppHeight()
        window.addEventListener('resize', updateAppHeight)

        return () => {
            window.removeEventListener('resize', updateAppHeight)
        }
    }, [])

    useEffect(() => {
        if (isAuthenticated) {
            socketClient.connect()
        }

        return () => {
            if (!isAuthenticated) {
                socketClient.disconnect()
            }
        }
    }, [isAuthenticated])

    // Обработка бана пользователя через WebSocket
    useEffect(() => {
        if (!isAuthenticated) return

        const handleUserBanned = async (data) => {
            console.warn('User has been banned:', data)
            // Перезагружаем данные пользователя чтобы Layout показал BanScreen
            try {
                await refreshUser()
            } catch (error) {
                console.error('Failed to refresh user after ban:', error)
            }
        }

        socketClient.on('user_banned', handleUserBanned)

        const handleLeaderboardReward = (data) => {
            if (!localStorage.getItem('onboardingCompleted')) return
            dispatch(openWindow(modalNames.LEADERBOARD_REWARD_MODAL, data))
        }
        socketClient.on('leaderboard_reward', handleLeaderboardReward)

        // [B.3] При заходе: проверяем невыданные награды лидерборда (юзер мог пропустить socket)
        const checkPendingRewards = async () => {
            if (!localStorage.getItem('onboardingCompleted')) return
            try {
                const { apiClient } = await import('../../core/http')
                const resp = await apiClient.get('/leaderboard/pending-rewards')
                const rewards = resp.data?.rewards || []
                if (rewards.length > 0) {
                    dispatch(openWindow(modalNames.LEADERBOARD_REWARD_MODAL, rewards[0]))
                }
            } catch (_) {}
        }
        checkPendingRewards()

        return () => {
            socketClient.off('user_banned', handleUserBanned)
            socketClient.off('leaderboard_reward', handleLeaderboardReward)
        }
    }, [isAuthenticated, refreshUser, dispatch])

    return (
        <SkeletonTheme
            baseColor="rgba(0, 0, 0, .2)"
            highlightColor="rgba(0, 0, 0, .2)"
            inline
        >
            {showSplash && (
                <SplashScreen
                    isLoading={isLoading}
                    onComplete={handleSplashComplete}
                />
            )}

            <Suspense fallback={<FullLoader />}>
                <HelmetProvider>
                    <Helmet>
                        <title>{t('app.title')}</title>
                    </Helmet>
                    <AppRoutes />
                    <Modals />
                </HelmetProvider>
            </Suspense>
            <Toasts />
        </SkeletonTheme>
    )
}

export default App
